import React, { useState, useCallback } from 'react';
import { Card } from 'react-bootstrap';
import { BannerList } from './components/BannerList';
import { BannerForm } from './components/BannerForm';
import { BannerPreview } from './components/BannerPreview';
import { Banner } from './types';
import PageHeader from '../../Common/PageHeader';
import Breadcrumbs from '../../Common/Breadcrumbs';
import CustomTabs from '../TournamentDetails/components/CustomTabs';
import '../TournamentDetails/components/CustomTabs.css';
import { getImageUrl } from '../../utils/imageUrl';

const tabOptions: {
  key: string;
  label: string;
  bannerLocation: 'home' | 'tournament' | 'playerVault';
  platform: 'app' | 'web';
}[] = [
  { key: 'home-app', label: 'Homepage - App', bannerLocation: 'home', platform: 'app' },
  { key: 'tournament-app', label: 'Tournament - App', bannerLocation: 'tournament', platform: 'app' },
  { key: 'home-web', label: 'Homepage - Web', bannerLocation: 'home', platform: 'web' },
  { key: 'tournament-web', label: 'Tournament - Web', bannerLocation: 'tournament', platform: 'web' },
];

const BannerManagement: React.FC = () => {
  const [selectedTab, setSelectedTab] = useState(tabOptions[0]);
  const [showForm, setShowForm] = useState(false);
  const [selectedBanner, setSelectedBanner] = useState<Banner | null>(null);
  const [showPreview, setShowPreview] = useState(false);
  const [previewBanner, setPreviewBanner] = useState<Banner | null>(null);
  const [refreshKey, setRefreshKey] = useState(0);
  // Per-tab search state
  const [searches, setSearches] = useState<{ [key: string]: string }>({});

  const handleAddBanner = () => {
    setSelectedBanner(null);
    setShowForm(true);
  };

  const handleEditBanner = (banner: Banner) => {
    setSelectedBanner(banner);
    setShowForm(true);
  };

  const handlePreviewBanner = (banner: Banner) => {
    setPreviewBanner(banner);
    setShowPreview(true);
  };

  const handleFormSuccess = useCallback(() => {
    setRefreshKey(prev => prev + 1);
  }, []);

  // Per-tab search handler
  const handleSearch = (term: string) => {
    setSearches(prev => ({ ...prev, [selectedTab.key]: term }));
    // Do NOT update refreshKey here; only update search state
  };

  return (
    <div className="container-fluid" style={{ marginTop: '20px' }}>
      <Breadcrumbs items={[{ label: 'All Banner Management', active: true }]} />
      <PageHeader
        breadcrumb={null}
        title="Banner Management"
        addNewLabel="Add New"
        onAddNew={handleAddBanner}
      />
      <CustomTabs
        tabs={tabOptions.map(({ key, label }) => ({ key, label }))}
        activeKey={selectedTab.key}
        onSelect={key => setSelectedTab(tabOptions.find(tab => tab.key === key)!)}
        orientation="horizontal"
        className="mb-3"
      />
      {tabOptions.map(tab => (
        selectedTab.key === tab.key && (
          <div className="row" key={tab.key}>
            <div className="col-12">
              <Card>
                <Card.Body>
                  <BannerList
                    key={tab.key}
                    onEdit={handleEditBanner}
                    onPreview={handlePreviewBanner}
                    search={searches[tab.key] || ''}
                    onSearch={handleSearch}
                    bannerLocation={tab.bannerLocation}
                    platform={tab.platform}
                  />
                </Card.Body>
              </Card>
            </div>
          </div>
        )
      ))}
      {showForm && (
        <BannerForm
          show={showForm}
          onHide={() => {
            setShowForm(false);
            setSelectedBanner(null);
          }}
          banner={selectedBanner}
          onSuccess={handleFormSuccess}
        />
      )}
      {showPreview && previewBanner && (
        <BannerPreview
          show={showPreview}
          onHide={() => setShowPreview(false)}
          banner={previewBanner}
          imageUrl={getImageUrl(previewBanner.image)}
        />
      )}
    </div>
  );
};

export default BannerManagement; 